import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import type { CardDefinitionQueryParams } from "src/app/features/CardImplementation/subFeatures/CardDefinition/services/server/CardDefinitionQueryTypes";
import { FormManager } from "../../../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../../../core/setup/redux/state/notifications/notificationsState";
import { useCardDefinitionLoadQuery, useCardDefinitionUpdateMutation } from "../../services/server/CardDefinitionApi";
import type { CardDefinitionEntity } from "../../types/CardDefinitionEntity";

export const CardDefinitionEditForm: FC<FormProps> = ({ runSubmit }) => {
    const navigate = useNavigate();
    const { id, cardImplementationId } = useParams();
    const [queryConfig, setQueryConfig] = useState<CardDefinitionQueryParams>({});

    useEffect(() => {
        setQueryConfig({ id, cardImplementationId });
    }, [id, cardImplementationId]);

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("card-definition");

    const [formData, setFormData] = useState<CardDefinitionEntity>({} as CardDefinitionEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useCardDefinitionLoadQuery({ id, cardImplementationId });

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCardDefinitionUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({
                cardDefinitionFormData: formData,
                cardDefinitionQuery: queryConfig,
            });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "CardDefinition",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.CARD_IMPLEMENTATIONS}/${FORM_ACTION.EDIT}/${cardImplementationId}`);
            }
        },
        [formData, saveFormData, queryConfig, dispatch, navigate, cardImplementationId],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<CardDefinitionEntity>
                    name="CardDefinitionEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
